/**
 * Defect type + per-parse collector.
 *
 * Every silent-recovery / partial-parse / out-of-spec site eventually
 * reports a {@link Defect} on the per-parse {@link DefectCollector}.
 * The collector is allocated fresh for every `parseEmail` /
 * `analyzeEmail` invocation so defects can never leak between parses
 * (see the no-leak assertion in the email-parser tests).
 *
 * Severities are conservative on purpose: anything previously silent
 * stays `info`. Sites that already render a user-visible warning (DSN
 * status, malformed DKIM signature) escalate to `warning`. `error` is
 * reserved for spec-violations the parser can't recover from.
 */

import type { DefectCode } from "./defect-codes";

/**
 * Pointer into the source bytes / parsed shape that produced the
 * defect. Kept as a structural alias here so this file stays free of
 * circular imports; theme O3 (Commit 2) extracts the canonical
 * `EvidenceSource` type into a sibling module and re-uses the same
 * fields.
 */
export interface DefectEvidence {
  layer: string;
  headerIndex?: number;
  partId?: string;
  byteRange?: [number, number];
  lineRange?: [number, number];
  fieldPath?: string;
}

/** Worst-of-class label for a single {@link Defect}. */
export type DefectSeverity = "info" | "warning" | "error";

/**
 * Layer that emitted the defect. Mirrors the spec's pipeline groupings
 * — `parser` for the bytes/MIME walker, `analysis` for the derived
 * shapes, `derivations` for the offline-only enrichments.
 */
export type DefectSource = "parser" | "analysis" | "derivations";

/** One captured anomaly. Immutable once pushed onto the collector. */
export interface Defect {
  /** Canonical code from {@link DEFECT}. */
  code: DefectCode;
  /** Layer that emitted the defect. */
  source: DefectSource;
  severity: DefectSeverity;
  /**
   * Short human-readable summary; the UI shows this in the Defects tab.
   *
   * A message that quotes a value out of the MESSAGE (a filename, an address)
   * must not interpolate it. It carries a `{name}` placeholder and puts the
   * value in {@link Defect.values}, and every prose surface renders it with
   * {@link renderDefectMessage}. Interpolating made the value unreachable by
   * the redaction engine, which is path-addressed: a strict export named
   * `invoice.pdf.exe` in `parsed/*.analysis.json` and `indexes/defects.csv`
   * while every other copy of that filename was tokenised.
   */
  message: string;
  /**
   * Message content quoted by {@link Defect.message}, one entry per `{name}`
   * placeholder. Absent on the great majority of defects, whose prose is
   * entirely ours; present wherever a value the user supplied is named.
   *
   * These are the redaction seam: a preset governs `defects[].values.filename`
   * like any other path, and rendering AFTER the policy has run puts the token
   * in the prose. Rendering before it would put the original there.
   */
  values?: Readonly<Record<string, string>>;
  /** Optional evidence pointer — header index, MIME path, byte range. */
  evidence?: DefectEvidence;
  /** Optional recovery note — what the parser did instead. */
  recovery?: string;
}

/**
 * Resolve a defect's `{name}` placeholders from {@link Defect.values}.
 *
 * Placeholder-free messages (almost all of them) come back unchanged, so every
 * consumer can call this unconditionally. A placeholder with no matching entry
 * is left standing rather than silently blanked: it is a template the emitter
 * got wrong, and a visible `{filename}` in the UI is a bug report, while an
 * empty pair of quotes reads like a message with no filename.
 */
export function renderDefectMessage(defect: {
  message: string;
  values?: Readonly<Record<string, string>>;
}): string {
  const values = defect.values;
  if (!values || !defect.message.includes("{")) return defect.message;
  return defect.message.replace(/\{([A-Za-z0-9_]+)\}/g, (whole, key: string) =>
    Object.prototype.hasOwnProperty.call(values, key) ? values[key]! : whole,
  );
}

/**
 * Render every message in a defect list, dropping the now-redundant `values`.
 *
 * Called at the governance boundary (see `governedModel()` in
 * `ExportActions.tsx`) so that every export downstream carries readable prose
 * built from whatever the policy left behind: originals at `none`, tokens at a
 * redacting preset. Exports therefore never have to know about placeholders,
 * and a new export format cannot leak by forgetting to render.
 */
export function renderDefects<
  T extends { message: string; values?: Readonly<Record<string, string>> },
>(defects: readonly T[]): T[] {
  return defects.map((d) => {
    if (!d.values) return d;
    const { values: _values, ...rest } = d;
    return { ...rest, message: renderDefectMessage(d) } as T;
  });
}

/**
 * Per-parse collector. Allocate fresh inside every `parseEmail` /
 * `analyzeEmail` call; never reuse across calls — the no-leak invariant
 * is part of the contract.
 */
export interface DefectCollector {
  push(defect: Defect): void;
  list(): readonly Defect[];
  clear(): void;
}

/** Construct an empty {@link DefectCollector}. */
export function createDefectCollector(): DefectCollector {
  const items: Defect[] = [];
  return {
    push(defect) {
      items.push(defect);
    },
    list() {
      return items as readonly Defect[];
    },
    clear() {
      items.length = 0;
    },
  };
}
